import {
	createSlice,
	createAsyncThunk,
	createEntityAdapter,
} from "@reduxjs/toolkit";
import { showLoader, hideLoader } from "store/misc/loaderSlice";
import { showMessage } from "store/misc/messageSlice";
import cartService from "services/cartService";
import { dispatchCartItemsChanged, dispatchCartItemRemoved } from "eventBus";
import { getCustomerToken, clearCustomerToken } from "./customerToken";

export const cartAdapter = createEntityAdapter({
	selectId: (lineItem) => lineItem.variantId,
});

export const findCart = createAsyncThunk(
	"vino/cart/findCart",
	async (customerToken, { dispatch }) => {
		let cart = await cartService.find(customerToken);

		if (!cart) {
			cart = await createCart(customerToken);
		}
		const { id, checkoutId, lineItems } = cart;
		dispatch(
			setCartId({
				id,
				checkoutId,
			})
		);
		return lineItems ? lineItems : [];
	}
);

export const fetchCartLineItems = createAsyncThunk(
	"vino/cart/fetchLineItems",
	async (arg, { getState }) => {
		const cartId = getState().vino.cart.id;
		return await cartService.fetchLineItems(cartId);
	}
);

export const createCart = async (customerToken) => {
	return cartService.create({ customerToken });
};

export const addCartLineItem = (lineItem) => async (dispatch, getState) => {
	try {
		dispatch(showLoader());
		const cart = getState().vino.cart;
		const id = await cartService.addItem(lineItem, cart.id);
		lineItem.id = id;
		dispatch(addLineItem(lineItem));
		dispatchCartItemChangedEvent(getState);
	} catch (e) {
		console.error(e);
		dispatch(
			showMessage({
				message: "Error occurred while adding",
				variant: "error",
			})
		);
	} finally {
		dispatch(hideLoader());
	}
};

export const updateCartLineItem = (lineItem) => async (dispatch, getState) => {
	try {
		dispatch(showLoader());
		if (lineItem.quantity > 0) {
			await cartService.updateItem(lineItem);
			const _lineItem = {
				id: lineItem.variantId,
				changes: { quantity: lineItem.quantity },
			};
			dispatch(updateLintItem(_lineItem));
		} else {
			await cartService.removeItem(lineItem.id);
			dispatch(removeLineItem(lineItem.variantId));
		}
		dispatchCartItemChangedEvent(getState);
	} catch (e) {
		console.error(e);
		dispatch(
			showMessage({
				message: "Error occurred while adding",
				variant: "error",
			})
		);
	} finally {
		dispatch(hideLoader());
	}
};

export const removeCartLineItem = (lineItem) => async (dispatch) => {
	await cartService.removeItem(lineItem.id);
	dispatch(removeLineItem(lineItem.variantId));
	dispatchCartItemRemoved(lineItem);
};

export const clearCart = () => (dispatch) => {
	clearCustomerToken();
	dispatch(resetCart());
};
const dispatchCartItemChangedEvent = (getState) => {
	const lineItems = selectCartLineItems(getState());
	dispatchCartItemsChanged(lineItems);
};

export const {
	selectAll: selectCartLineItems,
	selectById: selectCartLineItemById,
} = cartAdapter.getSelectors((state) => state.vino.cart);

const initialState = cartAdapter.getInitialState({
	customerToken: getCustomerToken(),
	id: null,
	checkoutId: null,
});

const cartSlice = createSlice({
	name: "vino/cart",
	initialState,
	reducers: {
		resetCart: (state) => {
			state.customerToken = getCustomerToken();
			state.id = null;
			state.checkoutId = null;
		},
		addLineItem: cartAdapter.addOne,
		updateLintItem: cartAdapter.updateOne,
		removeLineItem: cartAdapter.removeOne,
		setCartId: (state, action) => {
			const { id, checkoutId } = action.payload;
			state.id = id;
			state.checkoutId = checkoutId;
		},
	},
	extraReducers: {
		[findCart.fulfilled]: cartAdapter.setAll,
		[fetchCartLineItems.fulfilled]: cartAdapter.setAll,
	},
});

export const {
	addLineItem,
	updateLintItem,
	removeLineItem,
	resetCart,
	setCartId,
} = cartSlice.actions;

export default cartSlice.reducer;
